/**
 * PLANOS DE AQUECIMENTO — a régua que decide quando um número fica apto.
 *
 * É configurável sem alterar código de propósito: o plano é a hipótese
 * operacional, e hipótese que só muda com deploy não é ajustada, é ignorada.
 *
 * Duas coisas a tela não deixa acontecer: excluir plano que tem número
 * rodando nele (o número ficaria órfão no meio da esteira) e salvar etapa sem
 * atividade autorizada (o motor não teria o que executar e a etapa nunca
 * fecharia — parece "lento", é na verdade travado).
 */
import { useState } from "react";
import type { Etapa, Plano, Pool } from "@co/contracts";
import { api } from "../api.js";
import {
  Aviso,
  Botao,
  Campo,
  Carregando,
  Painel as Cartao,
  Selo,
  Tabela,
  Vazio,
  classeEntrada,
} from "../ui/base.js";
import { ErroAcao, ErroTela, useCarga } from "../ui/carga.js";
import { Chip, Definicoes, LinhaVazia, Rotulo } from "../ui/dados.js";
import { Modal } from "../ui/gaveta.js";
import { num, plural } from "../ui/formato.js";
import {
  ACAO_EM_FALHA,
  ACOES_EM_FALHA,
  APROVACAO,
  ATIVIDADES,
  DIAS_SEMANA,
  POOL,
  TIPO_ATIVIDADE,
  TOM_ACAO_EM_FALHA,
  rotuloAtividade,
} from "../ui/rotulos.js";

/* ── Forma do formulário ──────────────────────────────────────────── */

interface EtapaForm {
  ordem: number;
  nome: string;
  obrigatoria: boolean;
  diasMin: number;
  diasMax: number;
  atividades: string[];
  maxAtividadesDia: number;
  intervaloMinMinutos: number;
  minAtividades: number;
  minTaxaSucessoPct: number;
  acaoEmFalha: Etapa["acaoEmFalha"];
}

interface PlanoForm {
  nome: string;
  pool: Pool;
  janelaInicio: string;
  janelaFim: string;
  diasSemana: number[];
  fuso: string;
  diasMinimos: number;
  reavaliarDias: number;
  aprovacao: "automatica" | "manual";
  ativo: boolean;
  etapas: EtapaForm[];
}

function etapaNova(ordem: number): EtapaForm {
  return {
    ordem,
    nome: `Etapa ${ordem}`,
    obrigatoria: true,
    diasMin: 2,
    diasMax: 4,
    atividades: ["conexao"],
    maxAtividadesDia: 6,
    intervaloMinMinutos: 45,
    minAtividades: 6,
    minTaxaSucessoPct: 90,
    acaoEmFalha: "pausar",
  };
}

function planoNovo(): PlanoForm {
  return {
    nome: "",
    pool: "disparo",
    janelaInicio: "09:00",
    janelaFim: "18:00",
    diasSemana: [1, 2, 3, 4, 5],
    fuso: "America/Sao_Paulo",
    diasMinimos: 14,
    reavaliarDias: 7,
    aprovacao: "manual",
    ativo: true,
    etapas: [etapaNova(1)],
  };
}

function daPlano(p: Plano): PlanoForm {
  return {
    nome: p.nome,
    pool: p.pool,
    janelaInicio: p.janelaInicio,
    janelaFim: p.janelaFim,
    diasSemana: [...p.diasSemana],
    fuso: p.fuso,
    diasMinimos: p.diasMinimos,
    reavaliarDias: p.reavaliarDias,
    aprovacao: p.aprovacao,
    ativo: p.ativo,
    etapas: [...p.etapas]
      .sort((a, b) => a.ordem - b.ordem)
      .map((e, i) => ({
        ordem: i + 1,
        nome: e.nome,
        obrigatoria: e.obrigatoria,
        diasMin: e.diasMin,
        diasMax: e.diasMax,
        atividades: [...e.atividades],
        maxAtividadesDia: e.maxAtividadesDia,
        intervaloMinMinutos: e.intervaloMinMinutos,
        minAtividades: e.minAtividades,
        minTaxaSucessoPct: e.minTaxaSucessoPct,
        acaoEmFalha: e.acaoEmFalha,
      })),
  };
}

/* ── Tela ─────────────────────────────────────────────────────────── */

type Editor =
  | { tipo: "nenhum" }
  | { tipo: "editar"; id: string; form: PlanoForm; nomeOriginal: string }
  | { tipo: "novo"; form: PlanoForm }
  | { tipo: "excluir"; plano: Plano };

export function Planos(): JSX.Element {
  const { carga, recarregar } = useCarga(() => api.planos(), "planos");
  const [editor, setEditor] = useState<Editor>({ tipo: "nenhum" });

  if (carga.fase === "carregando") return <Carregando />;
  if (carga.fase === "erro")
    return <ErroTela mensagem={carga.mensagem} aoTentar={recarregar} />;

  const planos = carga.dados.itens;

  function fechar(): void {
    setEditor({ tipo: "nenhum" });
  }
  function concluir(): void {
    fechar();
    recarregar();
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-[12.5px] text-tinta-2 m-0 flex-1 min-w-[240px]">
          {plural(planos.length, "plano configurado", "planos configurados")}. A
          ordem das etapas é a ordem em que o número as cumpre.
        </p>
        <Botao
          tipo="principal"
          pequeno
          onClick={() => setEditor({ tipo: "novo", form: planoNovo() })}
        >
          Novo plano
        </Botao>
      </div>

      {planos.length === 0 ? (
        <Cartao titulo="Nenhum plano configurado">
          <Vazio>
            Sem plano, nenhum número avança. Crie o primeiro plano de
            aquecimento para a esteira começar a andar.
          </Vazio>
        </Cartao>
      ) : (
        planos.map((p) => (
          <CartaoPlano
            key={p.id}
            plano={p}
            aoEditar={() =>
              setEditor({
                tipo: "editar",
                id: p.id,
                form: daPlano(p),
                nomeOriginal: p.nome,
              })
            }
            aoDuplicar={() =>
              setEditor({
                tipo: "novo",
                form: { ...daPlano(p), nome: `${p.nome} (cópia)` },
              })
            }
            aoExcluir={() => setEditor({ tipo: "excluir", plano: p })}
          />
        ))
      )}

      {(editor.tipo === "novo" || editor.tipo === "editar") && (
        <EditorPlano
          titulo={
            editor.tipo === "editar"
              ? `Editar plano — ${editor.nomeOriginal}`
              : "Novo plano de aquecimento"
          }
          inicial={editor.form}
          aoFechar={fechar}
          aoSalvar={async (form) => {
            if (editor.tipo === "editar") await api.salvarPlano(editor.id, form);
            else await api.criarPlano(form);
            concluir();
          }}
        />
      )}

      {editor.tipo === "excluir" && (
        <ModalExcluir plano={editor.plano} aoFechar={fechar} aoExcluir={concluir} />
      )}
    </div>
  );
}

/* ── Cartão de leitura ────────────────────────────────────────────── */

function CartaoPlano({
  plano,
  aoEditar,
  aoDuplicar,
  aoExcluir,
}: {
  plano: Plano;
  aoEditar: () => void;
  aoDuplicar: () => void;
  aoExcluir: () => void;
}): JSX.Element {
  const emUso = plano.numerosUsando > 0;
  const etapas = [...plano.etapas].sort((a, b) => a.ordem - b.ordem);

  return (
    <Cartao
      titulo={plano.nome}
      dica={`${POOL[plano.pool]} · ${plural(plano.numerosUsando, "número usando", "números usando")}`}
      acoes={
        <>
          {plano.ativo ? (
            <Selo tom="ok">Ativo</Selo>
          ) : (
            <Selo tom="idle">Inativo</Selo>
          )}
          <Botao pequeno onClick={aoEditar}>
            Editar
          </Botao>
          <Botao pequeno tipo="fantasma" onClick={aoDuplicar}>
            Duplicar
          </Botao>
          <Botao
            pequeno
            tipo="perigo"
            desabilitado={emUso}
            titulo={
              emUso
                ? `${plural(plano.numerosUsando, "número usa", "números usam")} este plano — mover antes de excluir`
                : "Excluir o plano"
            }
            onClick={aoExcluir}
          >
            Excluir
          </Botao>
        </>
      }
      semCorpo
    >
      <div className="p-5 border-b border-linha">
        {emUso && (
          <div className="mb-4">
            <Aviso tom="idle">
              {plural(plano.numerosUsando, "número roda", "números rodam")} neste
              plano agora. Excluir está bloqueado: número sem plano fica parado
              no meio da esteira, sem etapa e sem critério de saída.
            </Aviso>
          </div>
        )}
        <Definicoes
          itens={[
            {
              k: "Janela",
              v: `${plano.janelaInicio} às ${plano.janelaFim} · ${plano.fuso}`,
            },
            {
              k: "Dias",
              v:
                plano.diasSemana.length === 0
                  ? "nenhum dia liberado — o plano não roda"
                  : plano.diasSemana
                      .slice()
                      .sort((a, b) => a - b)
                      .map((d) => DIAS_SEMANA[d] ?? d)
                      .join(", "),
            },
            { k: "Tempo mínimo", v: `${num(plano.diasMinimos)} dia(s) no plano` },
            { k: "Reavaliar", v: `a cada ${num(plano.reavaliarDias)} dia(s)` },
            { k: "Liberação", v: APROVACAO[plano.aprovacao] },
          ]}
        />
      </div>

      <Tabela
        larguraMin={980}
        colunas={[
          { chave: "o", rotulo: "#" },
          { chave: "n", rotulo: "Etapa" },
          { chave: "d", rotulo: "Dias", direita: true },
          { chave: "a", rotulo: "Ativ. mín. / máx. dia", direita: true },
          { chave: "i", rotulo: "Intervalo", direita: true },
          { chave: "t", rotulo: "Sucesso mín.", direita: true },
          { chave: "z", rotulo: "Atividades autorizadas" },
          { chave: "f", rotulo: "Se falhar" },
        ]}
      >
        {etapas.length === 0 ? (
          <LinhaVazia colunas={8}>
            Plano sem etapa: nenhum número consegue progredir nele.
          </LinhaVazia>
        ) : (
          etapas.map((e) => (
            <tr key={`${e.ordem}-${e.nome}`} className="border-t border-linha align-top">
              <td className="px-5 py-2.5 text-[12px] tabular text-tinta-3">
                {e.ordem}
              </td>
              <td className="px-5 py-2.5 text-[12.5px]">
                {e.nome}
                {!e.obrigatoria && (
                  <span className="text-tinta-3"> (opcional)</span>
                )}
              </td>
              <td className="px-5 py-2.5 text-right text-[12.5px] tabular">
                {e.diasMin}–{e.diasMax}
              </td>
              <td className="px-5 py-2.5 text-right text-[12.5px] tabular">
                {num(e.minAtividades)} / {num(e.maxAtividadesDia)}
              </td>
              <td className="px-5 py-2.5 text-right text-[12.5px] tabular">
                {num(e.intervaloMinMinutos)} min
              </td>
              <td className="px-5 py-2.5 text-right text-[12.5px] tabular">
                {num(e.minTaxaSucessoPct)}%
              </td>
              <td className="px-5 py-2.5">
                <div className="flex gap-1 flex-wrap">
                  {e.atividades.length === 0 ? (
                    <Selo tom="crit">Nenhuma — a etapa nunca fecha</Selo>
                  ) : (
                    e.atividades.map((a) => (
                      <Selo key={a} tom="idle">
                        {rotuloAtividade(a)}
                      </Selo>
                    ))
                  )}
                </div>
              </td>
              <td className="px-5 py-2.5">
                <Selo tom={TOM_ACAO_EM_FALHA[e.acaoEmFalha]}>
                  {ACAO_EM_FALHA[e.acaoEmFalha]}
                </Selo>
              </td>
            </tr>
          ))
        )}
      </Tabela>
    </Cartao>
  );
}

/* ── Editor ───────────────────────────────────────────────────────── */

/** Erros em texto: campo vermelho sem frase não diz o que arrumar. */
function validar(f: PlanoForm): string[] {
  const erros: string[] = [];
  if (f.nome.trim().length < 2) erros.push("O plano precisa de um nome.");
  if (f.diasSemana.length === 0)
    erros.push("Sem dia da semana liberado, o plano nunca executa nada.");
  if (f.etapas.length === 0) erros.push("O plano precisa de pelo menos uma etapa.");
  f.etapas.forEach((e, i) => {
    const onde = `Etapa ${i + 1}`;
    if (e.nome.trim().length < 2) erros.push(`${onde}: falta o nome.`);
    if (e.atividades.length === 0)
      erros.push(
        `${onde}: sem atividade autorizada o motor não tem o que executar e a etapa nunca fecha.`,
      );
    if (e.diasMax < e.diasMin)
      erros.push(`${onde}: a duração máxima não pode ser menor que a mínima.`);
    if (e.maxAtividadesDia < 1)
      erros.push(`${onde}: o máximo por dia precisa ser ao menos 1.`);
    if (e.intervaloMinMinutos < 1)
      erros.push(`${onde}: o intervalo mínimo precisa ser ao menos 1 minuto.`);
  });
  return erros;
}

function EditorPlano({
  titulo,
  inicial,
  aoFechar,
  aoSalvar,
}: {
  titulo: string;
  inicial: PlanoForm;
  aoFechar: () => void;
  aoSalvar: (form: PlanoForm) => Promise<void>;
}): JSX.Element {
  const [f, setF] = useState<PlanoForm>(inicial);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const erros = validar(f);

  function mudar(parcial: Partial<PlanoForm>): void {
    setF((atual) => ({ ...atual, ...parcial }));
  }

  function mudarEtapa(i: number, parcial: Partial<EtapaForm>): void {
    setF((atual) => ({
      ...atual,
      etapas: atual.etapas.map((e, idx) => (idx === i ? { ...e, ...parcial } : e)),
    }));
  }

  // A ordem é sempre reescrita a partir da posição: guardar a ordem digitada à
  // mão deixaria duas etapas com o mesmo número, e o motor não saberia qual vem
  // antes.
  function renumerar(etapas: EtapaForm[]): EtapaForm[] {
    return etapas.map((e, i) => ({ ...e, ordem: i + 1 }));
  }

  function adicionar(): void {
    setF((atual) => ({
      ...atual,
      etapas: renumerar([...atual.etapas, etapaNova(atual.etapas.length + 1)]),
    }));
  }

  function remover(i: number): void {
    setF((atual) => ({
      ...atual,
      etapas: renumerar(atual.etapas.filter((_, idx) => idx !== i)),
    }));
  }

  function mover(i: number, passo: -1 | 1): void {
    setF((atual) => {
      const destino = i + passo;
      if (destino < 0 || destino >= atual.etapas.length) return atual;
      const copia = [...atual.etapas];
      const a = copia[i];
      const b = copia[destino];
      if (!a || !b) return atual;
      copia[i] = b;
      copia[destino] = a;
      return { ...atual, etapas: renumerar(copia) };
    });
  }

  async function salvar(): Promise<void> {
    setErro(null);
    setSalvando(true);
    try {
      await aoSalvar({ ...f, nome: f.nome.trim(), etapas: renumerar(f.etapas) });
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível salvar o plano.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      titulo={titulo}
      largura={880}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            tipo="principal"
            desabilitado={erros.length > 0 || salvando}
            titulo={erros[0] ?? "Salvar o plano"}
            onClick={() => void salvar()}
          >
            {salvando ? "Salvando…" : "Salvar plano"}
          </Botao>
        </>
      }
    >
      {erro && <ErroAcao>{erro}</ErroAcao>}

      {erros.length > 0 && (
        <Aviso tom="warn">
          <strong>Falta resolver antes de salvar:</strong>
          <ul className="mt-1.5 mb-0 pl-4 list-disc">
            {erros.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </Aviso>
      )}

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
        <Campo rotulo="Nome do plano">
          <input
            value={f.nome}
            onChange={(e) => mudar({ nome: e.target.value })}
            className={classeEntrada}
            placeholder="Aquecimento padrão"
          />
        </Campo>
        <Campo rotulo="Pool">
          <select
            value={f.pool}
            onChange={(e) => mudar({ pool: e.target.value as Pool })}
            className={classeEntrada}
          >
            <option value="disparo">{POOL.disparo}</option>
            <option value="sdr">{POOL.sdr}</option>
          </select>
        </Campo>
        <Campo rotulo="Início da janela">
          <input
            type="time"
            value={f.janelaInicio}
            onChange={(e) => mudar({ janelaInicio: e.target.value })}
            className={classeEntrada}
          />
        </Campo>
        <Campo rotulo="Fim da janela">
          <input
            type="time"
            value={f.janelaFim}
            onChange={(e) => mudar({ janelaFim: e.target.value })}
            className={classeEntrada}
          />
        </Campo>
        <Campo rotulo="Fuso">
          <input
            value={f.fuso}
            onChange={(e) => mudar({ fuso: e.target.value })}
            className={classeEntrada}
          />
        </Campo>
        <CampoNumero
          rotulo="Tempo mínimo no plano (dias)"
          valor={f.diasMinimos}
          aoMudar={(v) => mudar({ diasMinimos: v })}
        />
        <CampoNumero
          rotulo="Reavaliar a cada (dias)"
          valor={f.reavaliarDias}
          aoMudar={(v) => mudar({ reavaliarDias: v })}
        />
        <Campo rotulo="Liberação comercial">
          <select
            value={f.aprovacao}
            onChange={(e) =>
              mudar({ aprovacao: e.target.value as "automatica" | "manual" })
            }
            className={classeEntrada}
          >
            <option value="automatica">{APROVACAO.automatica}</option>
            <option value="manual">{APROVACAO.manual}</option>
          </select>
        </Campo>
      </div>

      <div>
        <Rotulo>Dias permitidos</Rotulo>
        <div className="mt-2 flex gap-1.5 flex-wrap">
          {DIAS_SEMANA.map((d, i) => (
            <Chip
              key={d}
              ativo={f.diasSemana.includes(i)}
              onClick={() =>
                mudar({
                  diasSemana: f.diasSemana.includes(i)
                    ? f.diasSemana.filter((x) => x !== i)
                    : [...f.diasSemana, i],
                })
              }
            >
              {d}
            </Chip>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-[12.5px] text-tinta-2">
        <input
          type="checkbox"
          checked={f.ativo}
          onChange={(e) => mudar({ ativo: e.target.checked })}
        />
        Plano ativo — números novos podem ser atribuídos a ele
      </label>

      <div>
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <Rotulo>
            Etapas — {plural(f.etapas.length, "configurada", "configuradas")}
          </Rotulo>
          <Botao pequeno onClick={adicionar}>
            Adicionar etapa
          </Botao>
        </div>

        <div className="flex flex-col gap-3">
          {f.etapas.map((e, i) => (
            <CartaoEtapa
              key={i}
              indice={i}
              total={f.etapas.length}
              etapa={e}
              aoMudar={(parcial) => mudarEtapa(i, parcial)}
              aoRemover={() => remover(i)}
              aoMover={(passo) => mover(i, passo)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}

function CartaoEtapa({
  indice,
  total,
  etapa,
  aoMudar,
  aoRemover,
  aoMover,
}: {
  indice: number;
  total: number;
  etapa: EtapaForm;
  aoMudar: (parcial: Partial<EtapaForm>) => void;
  aoRemover: () => void;
  aoMover: (passo: -1 | 1) => void;
}): JSX.Element {
  return (
    <div className="border border-linha rounded-[10px] bg-superficie-2 p-3.5">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="rounded-md bg-marca text-superficie text-[11px] px-2 py-0.5 tabular font-medium">
          {indice + 1}
        </span>
        <input
          value={etapa.nome}
          onChange={(e) => aoMudar({ nome: e.target.value })}
          aria-label={`Nome da etapa ${indice + 1}`}
          className={`${classeEntrada} flex-1 min-w-[160px]`}
        />
        <label className="flex items-center gap-1.5 text-[11.5px] text-tinta-2">
          <input
            type="checkbox"
            checked={etapa.obrigatoria}
            onChange={(e) => aoMudar({ obrigatoria: e.target.checked })}
          />
          obrigatória
        </label>
        <Botao
          pequeno
          tipo="fantasma"
          desabilitado={indice === 0}
          titulo="Subir a etapa"
          onClick={() => aoMover(-1)}
        >
          Subir
        </Botao>
        <Botao
          pequeno
          tipo="fantasma"
          desabilitado={indice === total - 1}
          titulo="Descer a etapa"
          onClick={() => aoMover(1)}
        >
          Descer
        </Botao>
        <Botao pequeno tipo="fantasma" onClick={aoRemover}>
          Remover
        </Botao>
      </div>

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
        <CampoNumero
          rotulo="Duração mínima (dias)"
          valor={etapa.diasMin}
          aoMudar={(v) => aoMudar({ diasMin: v })}
        />
        <CampoNumero
          rotulo="Duração máxima (dias)"
          valor={etapa.diasMax}
          aoMudar={(v) => aoMudar({ diasMax: v })}
        />
        <CampoNumero
          rotulo="Máx. atividades por dia"
          valor={etapa.maxAtividadesDia}
          minimo={1}
          aoMudar={(v) => aoMudar({ maxAtividadesDia: v })}
        />
        <CampoNumero
          rotulo="Intervalo mínimo (min)"
          valor={etapa.intervaloMinMinutos}
          minimo={1}
          aoMudar={(v) => aoMudar({ intervaloMinMinutos: v })}
        />
        <CampoNumero
          rotulo="Atividades mínimas na etapa"
          valor={etapa.minAtividades}
          aoMudar={(v) => aoMudar({ minAtividades: v })}
        />
        <CampoNumero
          rotulo="Taxa de sucesso mínima (%)"
          valor={etapa.minTaxaSucessoPct}
          minimo={0}
          maximo={100}
          aoMudar={(v) => aoMudar({ minTaxaSucessoPct: v })}
        />
        <Campo rotulo="Se a etapa falhar">
          <select
            value={etapa.acaoEmFalha}
            onChange={(e) =>
              aoMudar({ acaoEmFalha: e.target.value as Etapa["acaoEmFalha"] })
            }
            className={classeEntrada}
          >
            {ACOES_EM_FALHA.map((a) => (
              <option key={a} value={a}>
                {ACAO_EM_FALHA[a]}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      <div className="mt-3">
        <Rotulo>Atividades autorizadas nesta etapa</Rotulo>
        <div className="mt-2 flex gap-1.5 flex-wrap">
          {ATIVIDADES.map((t) => (
            <Chip
              key={t}
              ativo={etapa.atividades.includes(t)}
              onClick={() =>
                aoMudar({
                  atividades: etapa.atividades.includes(t)
                    ? etapa.atividades.filter((x) => x !== t)
                    : [...etapa.atividades, t],
                })
              }
            >
              {TIPO_ATIVIDADE[t]}
            </Chip>
          ))}
        </div>
        {etapa.atividades.length === 0 && (
          <p className="text-[11.5px] text-crit mt-2 m-0">
            Sem atividade autorizada, o motor não tem o que executar e a etapa
            nunca fecha.
          </p>
        )}
      </div>
    </div>
  );
}

function CampoNumero({
  rotulo,
  valor,
  minimo = 0,
  maximo,
  aoMudar,
}: {
  rotulo: string;
  valor: number;
  minimo?: number;
  maximo?: number;
  aoMudar: (v: number) => void;
}): JSX.Element {
  return (
    <Campo rotulo={rotulo}>
      <input
        type="number"
        value={valor}
        min={minimo}
        max={maximo}
        onChange={(e) => {
          const v = Number(e.target.value);
          aoMudar(Number.isFinite(v) ? v : minimo);
        }}
        className={classeEntrada}
      />
    </Campo>
  );
}

/* ── Exclusão ─────────────────────────────────────────────────────── */

function ModalExcluir({
  plano,
  aoFechar,
  aoExcluir,
}: {
  plano: Plano;
  aoFechar: () => void;
  aoExcluir: () => void;
}): JSX.Element {
  const [erro, setErro] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState(false);

  async function excluir(): Promise<void> {
    setErro(null);
    setExcluindo(true);
    try {
      await api.excluirPlano(plano.id);
      aoExcluir();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível excluir.");
    } finally {
      setExcluindo(false);
    }
  }

  return (
    <Modal
      titulo={`Excluir o plano "${plano.nome}"`}
      largura={480}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            tipo="perigo"
            desabilitado={excluindo}
            onClick={() => void excluir()}
          >
            {excluindo ? "Excluindo…" : "Excluir plano"}
          </Botao>
        </>
      }
    >
      {erro && <ErroAcao>{erro}</ErroAcao>}
      <p className="text-[13px] text-tinta-2 m-0 leading-relaxed">
        Nenhum número usa este plano hoje. A exclusão não apaga o histórico dos
        números que já passaram por ele.
      </p>
    </Modal>
  );
}
